import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import ChatPanel from './ChatPanel';
import InteractiveLottieAvatar from './InteractiveLottieAvatar';
import {
  extractClassroomReadResultsFromActionResults,
  extractUploadRequestsFromActionResults,
  requestKimiReply,
  uploadAttachmentForKimiChat,
  type KimiChatContextMetadata,
  type KimiChatMessage,
  type PendingActionBundle,
  type SermonAgentActionResult,
  type SermonAgentUploadRequest,
} from '../../services/kimiChat';
import { uploadFileForSermonAgent } from '../../services/sermonAgent';

const CLASSROOM_ENTITY_TYPES = new Set<NonNullable<SermonAgentActionResult['entityType']>>([
  'classes',
  'lessons',
  'assignments',
  'quizzes',
  'class_enrollments',
  'submissions',
  'class_discussion_posts',
  'class_discussion_settings',
  'announcements',
  'class_syllabi',
  'class_textbooks',
]);

const CLASSROOM_ACTION_TYPES = new Set<string>([
  'create_class',
  'update_class',
  'delete_class',
  'upsert_lesson',
  'upsert_lesson_v2',
  'delete_lesson',
  'upsert_work',
  'upsert_work_v2',
  'delete_work',
  'enroll_students',
  'unenroll_students',
  'grade_submission',
  'set_discussion_lock',
  'moderate_discussion_post',
  'create_announcement',
  'upsert_class_syllabus',
  'delete_class_syllabus',
  'create_class_textbook',
  'update_class_textbook',
  'delete_class_textbook',
]);

const hasSuccessfulClassroomMutation = (actionResults: SermonAgentActionResult[]): boolean =>
  actionResults.some(
    (result) =>
      result.status === 'success' &&
      ((typeof result.entityType === 'string' && CLASSROOM_ENTITY_TYPES.has(result.entityType)) ||
        CLASSROOM_ACTION_TYPES.has(result.type))
  );

const CONTENT_ENTITY_TYPES = new Set<NonNullable<SermonAgentActionResult['entityType']>>([
  'trivia_quizzes',
  'devotional_series',
  'devotionals',
]);

const CONTENT_ACTION_TYPES = new Set<string>([
  'upsert_trivia_quiz',
  'create_devotional_series',
  'upsert_devotional',
]);

const hasSuccessfulContentMutation = (actionResults: SermonAgentActionResult[]): boolean =>
  actionResults.some(
    (result) =>
      result.status === 'success' &&
      ((typeof result.entityType === 'string' && CONTENT_ENTITY_TYPES.has(result.entityType)) ||
        CONTENT_ACTION_TYPES.has(result.type))
  );

const invalidateContentQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: ['trivia', 'admin', 'active'] });
  void queryClient.invalidateQueries({ queryKey: ['devotionals'] });
  void queryClient.invalidateQueries({ queryKey: ['devotionalSeries'] });
};

const KimiChatWidget: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<KimiChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingActionId, setUploadingActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contextMetadata, setContextMetadata] = useState<KimiChatContextMetadata | null>(null);
  const [uploadRequests, setUploadRequests] = useState<SermonAgentUploadRequest[]>([]);
  const [pendingActionBundle, setPendingActionBundle] = useState<PendingActionBundle | null>(null);

  const avatarState = useMemo<'idle' | 'listening' | 'thinking' | 'responding'>(() => {
    if (isLoading) return 'thinking';
    if (!open) return 'idle';
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant') return 'responding';
    return 'listening';
  }, [isLoading, open, messages]);

  if (!user) {
    return null;
  }

  const handleSend = async (text: string, attachments: File[]) => {
    const trimmedText = text.trim();
    setError(null);
    setIsLoading(true);

    try {
      const uploadedAttachments = attachments.length
        ? await Promise.all(attachments.map((file) => uploadAttachmentForKimiChat(file)))
        : [];
      const userMessage: KimiChatMessage = {
        role: 'user',
        content: trimmedText || 'Shared an attachment for review.',
        ...(uploadedAttachments.length ? { attachments: uploadedAttachments } : {}),
      };
      const nextHistory = [...messages, userMessage];
      setMessages(nextHistory);

      const response = await requestKimiReply(nextHistory);
      setContextMetadata(response.context ?? null);
      setUploadRequests(extractUploadRequestsFromActionResults(response.actionResults));
      setPendingActionBundle(response.pendingActionBundle);
      const classroomReadResults = extractClassroomReadResultsFromActionResults(response.actionResults);
      if (hasSuccessfulClassroomMutation(response.actionResults)) {
        void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher'] });
      }
      if (hasSuccessfulContentMutation(response.actionResults)) {
        invalidateContentQueries(queryClient);
      }
      setMessages((previous: KimiChatMessage[]) => [
        ...previous,
        {
          role: 'assistant',
          content: response.assistantMessage,
          ...(classroomReadResults.length ? { classroomReadResults } : {}),
        },
      ]);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to get a response right now.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmPendingChanges = async () => {
    if (!pendingActionBundle) return;
    setError(null);
    setIsLoading(true);
    try {
      const response = await requestKimiReply(messages, {
        confirmActionBundle: pendingActionBundle.token,
      });
      setContextMetadata(response.context ?? null);
      setUploadRequests(extractUploadRequestsFromActionResults(response.actionResults));
      setPendingActionBundle(null);
      const classroomReadResults = extractClassroomReadResultsFromActionResults(response.actionResults);
      if (hasSuccessfulClassroomMutation(response.actionResults)) {
        void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher'] });
      }
      if (hasSuccessfulContentMutation(response.actionResults)) {
        invalidateContentQueries(queryClient);
      }
      setMessages((previous: KimiChatMessage[]) => [
        ...previous,
        {
          role: 'assistant',
          content: response.assistantMessage,
          ...(classroomReadResults.length ? { classroomReadResults } : {}),
        },
      ]);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to confirm these changes right now.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPendingChanges = () => {
    setPendingActionBundle(null);
    setMessages((previous: KimiChatMessage[]) => [
      ...previous,
      {
        role: 'assistant',
        content: 'Canceled pending changes.',
      },
    ]);
  };

  const handleUploadFile = async (request: SermonAgentUploadRequest, file: File) => {
    setError(null);
    setUploadingActionId(request.actionId);
    try {
      await uploadFileForSermonAgent(request, file);
      setUploadRequests((previous: SermonAgentUploadRequest[]) =>
        previous.filter((entry: SermonAgentUploadRequest) => entry.actionId !== request.actionId)
      );
      setMessages((previous: KimiChatMessage[]) => [
        ...previous,
        {
          role: 'assistant',
          content: `Uploaded and attached successfully to ${request.target.replace('_', ' ')}.`,
        },
      ]);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Unable to upload this file right now.');
    } finally {
      setUploadingActionId(null);
    }
  };

  return (
    <>
      <ChatPanel
        open={open}
        messages={messages}
        isLoading={isLoading}
        error={error}
        contextMetadata={contextMetadata}
        uploadRequests={uploadRequests}
        uploadingActionId={uploadingActionId}
        pendingActionBundle={pendingActionBundle}
        onUploadFile={handleUploadFile}
        onConfirmPendingChanges={handleConfirmPendingChanges}
        onCancelPendingChanges={handleCancelPendingChanges}
        onSend={handleSend}
        onClose={() => setOpen(false)}
      />
      <InteractiveLottieAvatar
        isOpen={open}
        state={avatarState}
        onToggle={() => {
          setOpen((previous: boolean) => !previous);
          setError(null);
        }}
      />
    </>
  );
};

export default KimiChatWidget;
